import {
  type JsonObject,
  type MdbaseConnection,
  type QueryRecord,
  type RecordDocument,
} from "@mdbase-dev/connect";
import { addDays, localDateKey } from "./cycle";
import { connectProblemError } from "./connect-outcomes";
import type {
  CycleDay,
  CycleDayInput,
  CycleRepository,
  Energy,
  Flow,
  Mood,
  Symptom,
} from "./types";

const CONTRACT_ID = "dev.callumalpass.cycle.day";
const CONTRACT_VERSION = "1.0.0";

function contract(provider?: string) {
  return {
    id: CONTRACT_ID,
    version: CONTRACT_VERSION,
    ...(provider ? { type: provider } : {}),
  };
}

function asCycleDay(
  value: QueryRecord<JsonObject> | RecordDocument<JsonObject>,
  fallbackPath: string,
): CycleDay {
  const frontmatter =
    value.effectiveFrontmatter && typeof value.effectiveFrontmatter === "object"
      ? value.effectiveFrontmatter
      : value.frontmatter && typeof value.frontmatter === "object"
        ? value.frontmatter
        : {};
  return {
    path: String(value.path ?? fallbackPath),
    revision:
      "revision" in value && typeof value.revision === "string"
        ? value.revision
        : undefined,
    contractType: value.contract?.type,
    date: String(frontmatter.date ?? ""),
    flow: String(frontmatter.flow ?? "none") as Flow,
    symptoms: Array.isArray(frontmatter.symptoms)
      ? frontmatter.symptoms.map(String) as Symptom[]
      : [],
    pain: Number(frontmatter.pain ?? 0),
    mood: String(frontmatter.mood ?? "steady") as Mood,
    energy: String(frontmatter.energy ?? "medium") as Energy,
    sleep_quality: Number(frontmatter.sleep_quality ?? 3),
    notes: String(frontmatter.notes ?? ""),
    updated_at: String(frontmatter.updated_at ?? ""),
  };
}

export class MdbaseCycleRepository implements CycleRepository {
  constructor(private readonly connection: MdbaseConnection) {}

  async list(): Promise<CycleDay[]> {
    await this.recoverPendingMutations();
    const outcome = await this.connection.queryAll(
      { contract: contract() },
      { firstPageSize: 500, pageSize: 500 },
    );
    if (!outcome.ok) throw connectProblemError(outcome.problem);
    return outcome.value.results
      .map((row) => asCycleDay(row, row.path))
      .sort((left, right) => left.date.localeCompare(right.date));
  }

  async save(input: CycleDayInput, existing?: CycleDay): Promise<CycleDay> {
    if (this.connection.pendingMutations().length > 0) {
      throw new Error(
        "Reload Tide to recover the exact pending save before making another change.",
      );
    }
    const path = existing?.path ?? `cycle-days/${input.date}.md`;
    const frontmatter = {
      ...input,
      updated_at: new Date().toISOString(),
    } satisfies JsonObject;

    if (existing) {
      const selector = contract(existing.contractType);
      const current = await this.connection.read({ path, contract: selector });
      if (!current.ok) throw connectProblemError(current.problem);
      const outcome = await this.connection.update({
        path,
        patch: frontmatter,
        ifRevision: current.value.revision,
        contract: selector,
      });
      if (!outcome.ok) throw connectProblemError(outcome.problem);
      return asCycleDay(outcome.value, path);
    }

    const described = await this.connection.describe();
    if (!described.ok) throw connectProblemError(described.problem);
    const descriptor = described.value.contracts.find(
      (candidate) =>
        candidate.id === CONTRACT_ID &&
        candidate.version === CONTRACT_VERSION,
    );
    const providers = [...(descriptor?.implementations ?? [])].sort((left, right) =>
      left.typeName.localeCompare(right.typeName),
    );
    const provider =
      providers.find((candidate) => candidate.typeName === "cycle-day") ??
      providers[0];
    if (!provider) {
      throw new Error("This collection does not implement the Tide cycle-day contract.");
    }
    const outcome = await this.connection.create({
      path,
      frontmatter,
      contract: contract(provider.typeName),
    });
    if (!outcome.ok) throw connectProblemError(outcome.problem);
    return asCycleDay(outcome.value, path);
  }

  private async recoverPendingMutations(): Promise<void> {
    for (const pending of this.connection.pendingMutations()) {
      const outcome = await pending.recover();
      if (!outcome.ok) throw connectProblemError(outcome.problem);
    }
  }
}

export class DemoCycleRepository implements CycleRepository {
  private days = demoDays();

  async list(): Promise<CycleDay[]> {
    return this.days.map((day) => ({ ...day, symptoms: [...day.symptoms] }));
  }

  async save(input: CycleDayInput, existing?: CycleDay): Promise<CycleDay> {
    const saved: CycleDay = {
      ...input,
      path: existing?.path ?? `cycle-days/${input.date}.md`,
      revision: String(Number(existing?.revision ?? "0") + 1),
      updated_at: new Date().toISOString(),
    };
    this.days = [
      ...this.days.filter((day) => day.date !== input.date),
      saved,
    ].sort((left, right) => left.date.localeCompare(right.date));
    return saved;
  }
}

function demoDays(): CycleDay[] {
  const today = localDateKey();
  const starts = [-86, -57, -28];
  const records: CycleDay[] = [];
  for (const start of starts) {
    for (let periodDay = 0; periodDay < 5; periodDay += 1) {
      const date = addDays(today, start + periodDay);
      records.push({
        path: `cycle-days/${date}.md`,
        revision: "1",
        date,
        flow: (["heavy", "medium", "medium", "light", "spotting"] as Flow[])[
          periodDay
        ],
        symptoms:
          periodDay === 0
            ? ["cramps", "fatigue"]
            : periodDay === 1
              ? ["cramps", "bloating"]
              : [],
        pain: Math.max(0, 3 - periodDay),
        mood: periodDay < 2 ? "sensitive" : "steady",
        energy: periodDay < 2 ? "low" : "medium",
        sleep_quality: periodDay === 0 ? 2 : 3,
        notes: periodDay === 0 && start === -28 ? "Heat pack helped." : "",
        updated_at: new Date().toISOString(),
      });
    }
  }
  const yesterday = addDays(today, -1);
  records.push({
    path: `cycle-days/${yesterday}.md`,
    revision: "1",
    date: yesterday,
    flow: "none",
    symptoms: ["tenderness"],
    pain: 0,
    mood: "steady",
    energy: "high",
    sleep_quality: 4,
    notes: "",
    updated_at: new Date().toISOString(),
  });
  return records.sort((left, right) => left.date.localeCompare(right.date));
}
